import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../../components/buttons/Button";
import UserAvatar from "../../components/utils/UserAvatar";
import LangButton from "../../components/buttons/LangButton";
import RestaurantAppPreview from "../../components/app/RestaurantAppPreview";
import SettingsContext from "../../contexts/SettingsContext";
import NotificationsList from "../notifications/NotificationsList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faBell,
  faCircleQuestion,
} from "@fortawesome/pro-light-svg-icons";
import { faPlusCircle, faCircle } from "@fortawesome/pro-solid-svg-icons";
import { useAuth } from "../../hooks/useAuth";
import useWebSocket from "../../hooks/useWebSocket";
import useApi from "../../hooks/useApi";
import {
  getRestaurant,
  putSwitchRestaurant,
  putSwitchCatering,
} from "../../services/restaurantService";
import { useSelector, useDispatch } from "react-redux";
import NavigationContext from "../../contexts/NavigationContext";
import {
  stopControllingRestaurant,
  stopControllingCompany,
} from "../../redux/slices/adminSlice";
import useLangNavigate from "../../hooks/useLangNavigate";
import { setRestaurantId } from "../../redux/slices/userSlice";
import { faCode } from "@fortawesome/pro-regular-svg-icons";
import { consumptionIds } from "../../utils/constants";
import TourLauncher from "../tour/TourLauncher";
import { isDemoMode } from "../../utils/demoApiInterceptor";

const Navbar = ({
  navigateBack = null,
  activationDetails = null,
}) => {
  const { logout, isRestaurantAdmin, isOffoodAdmin, isPureRestaurantAdmin } =
    useAuth();
  const navigate = useLangNavigate();
  const { t } = useTranslation();
  const { activeTab, isValid, applyChanges, hideSettingsButton } =
    useContext(SettingsContext);
  const { hasChanged } = useContext(NavigationContext);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showRestaurantMenu, setShowRestaurantMenu] = useState(false);
  const [isOnOverview, setIsOnOverview] = useState(false);
  const [isRestaurantActive, setIsRestaurantActive] = useState(false);
  const [isCateringActive, setIsCateringActive] = useState(false);
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const restaurants = useSelector((state) => state.user.restaurants);
  const {
    controlledRestaurantId,
    controlledConsumptionModes,
    isControllingEntity,
  } = useSelector((state) => state.admin);
  const controlledCompanyId = useSelector(
    (state) => state.admin.controlledCompanyId,
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const { request: fetchRestaurant } = useApi(getRestaurant);
  const { request: switchRestaurant } = useApi(putSwitchRestaurant);
  const { request: switchCatering } = useApi(putSwitchCatering);
  const user = useSelector((state) => state.user);
  const { notifications, setNotifications } = useWebSocket(
    "notifications",
    user.userId,
  );
  const userMenuRef = useRef(null);
  const restaurantMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const isStripeDone = activationDetails?.isStripeDone ?? null;
  const stripeUrl = activationDetails?.stripeUrl ?? null;
  const canActivateRestaurant = activationDetails?.canActivateRestaurant ?? false;
  const activationLoading = activationDetails?.loading ?? false;
  const firstMissingStep = activationDetails?.missingSteps?.[0] ?? null;
  const firstMissingStepRoute = activationDetails?.firstMissingStepRoute ?? null;
  const navigateToActivationStep = useCallback(() => {
    if (firstMissingStep?.key === "banking" && stripeUrl) {
      window.open(stripeUrl, "_blank");
      return;
    }
    navigate(firstMissingStepRoute || "dashboard/settings");
  }, [firstMissingStep, firstMissingStepRoute, navigate, stripeUrl]);

  const handleClickOutside = useCallback((event) => {
    if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
      setShowUserMenu(false);
    }
    if (
      restaurantMenuRef.current &&
      !restaurantMenuRef.current.contains(event.target)
    ) {
      setShowRestaurantMenu(false);
    }
    if (
      notificationsRef.current &&
      !notificationsRef.current.contains(event.target)
    ) {
      setShowNotifications(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleClickOutside);
    return () => {
      document.removeEventListener("mouseup", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    setIsOnOverview(location.pathname.endsWith("/dashboard"));
  }, [location.pathname]);

  useEffect(() => {
    if (
      isOnOverview &&
      (isRestaurantAdmin || isOffoodAdmin) &&
      !!restaurantId
    ) {
      fetchRestaurant(restaurantId).then((response) => {
        const restaurant = response.data;
        setIsRestaurantActive(restaurant.isActive);
        setIsCateringActive(restaurant.isCateringActive);
      });
    }
  }, [
    isRestaurantAdmin,
    isOnOverview,
    isRestaurantActive,
    isCateringActive,
    isOffoodAdmin,
    restaurantId,
    fetchRestaurant,
  ]);

  const handleSwitchRestaurant = () => {
    if (!isRestaurantActive && !canActivateRestaurant) {
      toast.warning(t("dashboard.restaurant.activationIncomplete"));
      navigateToActivationStep();
      return;
    }
    switchRestaurant(restaurantId)
      .then((response) => {
        setIsRestaurantActive(response.data.isActive);
        toast.success(
          t(
            `dashboard.restaurant.toasts.switchRestaurant${
              response.data.isActive ? "Active" : "Inactive"
            }`,
          ),
        );
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  const handleSwitchCatering = () => {
    if (!isCateringActive && !canActivateRestaurant) {
      toast.warning(t("dashboard.restaurant.activationIncomplete"));
      navigateToActivationStep();
      return;
    }
    switchCatering(restaurantId)
      .then((response) => {
        setIsCateringActive(response.data.isCateringActive);
        toast.success(
          t(
            `dashboard.restaurant.toasts.switchCatering${
              response.data.isCateringActive ? "Active" : "Inactive"
            }`,
          ),
        );
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  const handleNavigateBackWithConfirmation = () => {
    if (hasChanged) {
      const confirmNavigation = window.confirm(
        t("utils.navigation.confirmLeaveWithUnsavedChanges"),
      );
      if (confirmNavigation) navigate(navigateBack);
      else return;
    } else navigate(navigateBack);
  };

  const handleStopControllingRestaurant = () => {
    dispatch(stopControllingRestaurant());
    navigate("dashboard");
  };

  const handleStopControllingCompany = () => {
    dispatch(stopControllingCompany());
    navigate("dashboard");
  };

  // Reset notification count whenever the panel closes, regardless of how
  useEffect(() => {
    if (!showNotifications && notifications > 0) {
      setNotifications(0);
    }
  }, [showNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const handleSelectRestaurant = (restaurantId) => {
    dispatch(setRestaurantId(restaurantId));
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const showCateringButton = useCallback(() => {
    if (controlledRestaurantId) {
      controlledConsumptionModes?.find((mode) =>
        consumptionIds.CATERING.includes(mode.consumptionModeId),
      );
    } else {
      const restaurant = restaurants?.find(
        (r) => r.restaurantId === restaurantId,
      );
      return restaurant?.consumptionmodes?.find((mode) =>
        consumptionIds.CATERING.includes(mode.consumptionModeId),
      );
    }
  }, [
    restaurantId,
    restaurants,
    controlledRestaurantId,
    controlledConsumptionModes,
  ]);

  return (
    <div id="navbar">
      <div className="flex gap-1">
        {navigateBack && (
          <Button variant="light" onClick={handleNavigateBackWithConfirmation}>
            <FontAwesomeIcon size="xl" icon={faArrowLeftLong} />
            <span className="ml-2">{t("utils.common.back")}</span>
          </Button>
        )}
        {controlledRestaurantId && isOffoodAdmin && (
          <Button variant="primary" onClick={handleStopControllingRestaurant}>
            {t("dashboard.admin.clearControlledRestaurant")}
          </Button>
        )}
        {controlledCompanyId && isOffoodAdmin && (
          <Button variant="primary" onClick={handleStopControllingCompany}>
            {t("dashboard.admin.clearControlledCompany")}
          </Button>
        )}
        {activeTab && !hideSettingsButton && (
          <Button variant="success" onClick={applyChanges} disabled={!isValid}>
            {t(`dashboard.settings.navbarButton`)}
          </Button>
        )}
        {isOnOverview &&
          (isRestaurantAdmin || (isOffoodAdmin && !!controlledRestaurantId)) &&
          !activationLoading &&
          (isStripeDone === false ? (
            <span data-tour="stripe-button">
              <Button
                variant="danger"
                onClick={() => {
                  if (stripeUrl) {
                    window.open(stripeUrl, "_blank");
                  } else {
                    navigate("dashboard/settings/bank");
                  }
                }}
              >
                {t(`dashboard.restaurant.stripeNotConnected`)}
              </Button>
            </span>
          ) : isStripeDone === true ? (
            <>
              {!isRestaurantActive ? (
                <Button
                  variant={canActivateRestaurant ? "success" : "light"}
                  disabled={!canActivateRestaurant}
                  title={
                    !canActivateRestaurant
                      ? t("dashboard.restaurant.activationIncomplete")
                      : undefined
                  }
                  onClick={() => handleSwitchRestaurant()}
                >
                  {t("dashboard.restaurant.switchRestaurantActive")}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  onClick={() => handleSwitchRestaurant()}
                >
                  {t("dashboard.restaurant.switchRestaurantInactive")}
                </Button>
              )}
              {showCateringButton() && (
                <>
                  {!isCateringActive ? (
                    <Button
                      variant={canActivateRestaurant ? "success" : "light"}
                      disabled={!canActivateRestaurant}
                      title={
                        !canActivateRestaurant
                          ? t("dashboard.restaurant.activationIncomplete")
                          : undefined
                      }
                      onClick={() => handleSwitchCatering()}
                    >
                      {t("dashboard.restaurant.switchCateringActive")}
                    </Button>
                  ) : (
                    <Button
                      variant="danger"
                      onClick={() => handleSwitchCatering()}
                    >
                      {t("dashboard.restaurant.switchCateringInactive")}
                    </Button>
                  )}
                </>
              )}
            </>
          ) : null)}
      </div>
      <div className="ml-auto flex gap-1">
        {!isControllingEntity && isPureRestaurantAdmin && <TourLauncher />}
        {!isControllingEntity && (
          <Button
            className="gap-1"
            variant="info"
            onClick={() => navigate("support")}
          >
            <FontAwesomeIcon icon={faCircleQuestion} size="lg" />
            <span>{t("utils.common.support")}</span>
          </Button>
        )}
        <Button variant="light" onClick={handleShowNotifications}>
          <span className="fa-layers fa-fw">
            <FontAwesomeIcon color="black" size="xl" icon={faBell} />
            {notifications > 0 && (
              <FontAwesomeIcon
                icon={faCircle}
                size="xs"
                color="red"
                transform="right-10 up-10"
              />
            )}
          </span>
        </Button>
        {showNotifications && (
          <div ref={notificationsRef} id="notifications">
            <NotificationsList
              userId={user.userId}
              closeNotifications={() => setShowNotifications(false)}
            />
          </div>
        )}
        {isRestaurantAdmin && restaurantId && !controlledRestaurantId && (
          <div
            className="px-1 flex gap-1 align-center bg-pastel-light rounded cursor-pointer"
            onClick={() => setShowRestaurantMenu(!showRestaurantMenu)}
          >
            <RestaurantAppPreview
              isAvatar
              size="40"
              img={
                restaurants.find((r) => r.restaurantId === restaurantId)
                  .images?.[0]?.fullImagePath
              }
              restaurantType={
                restaurants.find((r) => r.restaurantId === restaurantId)
                  .restauranttype
              }
            />
            <span>
              {restaurants.find((r) => r.restaurantId === restaurantId).name
                .length > 12
                ? `${restaurants
                    .find((r) => r.restaurantId === restaurantId)
                    .name.slice(0, 9)}...`
                : restaurants.find((r) => r.restaurantId === restaurantId).name}
            </span>
          </div>
        )}
        {showRestaurantMenu && (
          <div
            id="restaurantmenu"
            className="flex column gap-1"
            ref={restaurantMenuRef}
          >
            <p className="bold">
              {restaurants.find((r) => r.restaurantId === restaurantId).name}
            </p>
            {restaurants
              .filter((r) => r.restaurantId !== restaurantId)
              .map((restaurant) => (
                <Button
                  variant="tertiary"
                  className="p-4 justify-start gap-2"
                  block
                  onClick={() =>
                    handleSelectRestaurant(restaurant.restaurantId)
                  }
                  key={restaurant.restaurantId}
                >
                  <RestaurantAppPreview
                    size="45"
                    img={restaurant.images?.[0]?.fullImagePath}
                    restaurantType={restaurant.restauranttype}
                  />
                  <span>
                    {restaurant.name.length > 12
                      ? `${restaurant.name.slice(0, 9)}...`
                      : restaurant.name}
                  </span>
                </Button>
              ))}
            <Button
              size="sm"
              block
              onClick={() =>
                navigate("register", { state: { initialStep: 2 } })
              }
            >
              <div className="flex gap-1 align-center">
                <FontAwesomeIcon icon={faPlusCircle} />
                <span>{t("dashboard.restaurant.addRestaurant")}</span>
              </div>
            </Button>
          </div>
        )}
        <UserAvatar
          className="cursor-pointer"
          src={user.imagePath}
          initiales={`${user.firstname[0]}${user.lastname[0]}`}
          size="60"
          onClick={() => setShowUserMenu(!showUserMenu)}
        />
        {showUserMenu && (
          <div id="usermenu" ref={userMenuRef} className="flex column gap-1">
            <p className="bold">
              {user.firstname} {user.lastname}
            </p>
            <LangButton noFlag={false} />
            <Button
              className="text-left"
              link
              block
              onClick={() => navigate("support")}
            >
              {t("utils.common.support")}
            </Button>
            {isRestaurantAdmin && (
              <Button
                className="text-left"
                link
                block
                onClick={() => navigate("dashboard/api")}
              >
                <FontAwesomeIcon icon={faCode} className="mr-2" />
                <span>{t("dashboard.api.sidebar")}</span>
              </Button>
            )}
            {!isDemoMode() && (
              <Button className="text-left" link block onClick={logout}>
                {t("dashboard.logout.sidebar")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Navbar;
